import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Button,
  Skeleton,
  Alert,
  LinearProgress,
} from "@mui/material";
import AppsIcon from "@mui/icons-material/Apps";
import DnsIcon from "@mui/icons-material/Dns";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import TimerIcon from "@mui/icons-material/Timer";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CategoryIcon from "@mui/icons-material/Category";

import {
  useAppDispatch,
  useAppSelector,
  fetchServices,
  fetchUserInstances,
  fetchActiveInstances,
} from "../store";
import { instanceService } from "../services";
import { InstanceCard } from "../components/instances";
import { ServiceInstance, UserResourceStats } from "../types";

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { services, isLoading: servicesLoading } = useAppSelector(
    (state) => state.services,
  );
  const {
    instances,
    activeInstances,
    activeCount,
    isLoading: instancesLoading,
  } = useAppSelector((state) => state.instances);

  const [stats, setStats] = useState<UserResourceStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(
    new Set(),
  );

  useEffect(() => {
    dispatch(fetchServices({ page: 0, size: 50 }));
    if (user) {
      dispatch(fetchUserInstances({ userId: user.id, page: 0, size: 50 }));
      dispatch(fetchActiveInstances(user.id));
    }
  }, [dispatch, user]);

  // Fetch user resource stats
  useEffect(() => {
    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const s = await instanceService.getMyStats();
        setStats(s);
      } catch {
        setStats(null);
      } finally {
        setStatsLoading(false);
      }
    };
    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, []);

  const isLoading = servicesLoading || instancesLoading;
  const failedInstances = (instances || []).filter(
    (i: ServiceInstance) => i.status === "FAILED",
  );
  const expiringInstances = (activeInstances || []).filter(
    (i: ServiceInstance) => {
      const remaining = new Date(i.expiresAt).getTime() - Date.now();
      return remaining > 0 && remaining < 3600000;
    },
  );

  const maxInstances = stats?.maxInstancesAllowed || 3;
  const usagePercent = stats
    ? Math.round((stats.activeInstanceCount / maxInstances) * 100)
    : 0;

  const topStats = [
    {
      label: "Available Services",
      value: services.length,
      icon: <AppsIcon sx={{ fontSize: 28 }} />,
      color: "#3b82f6",
      bgFrom: "#eff6ff",
      bgTo: "#dbeafe",
      border: "#bfdbfe",
      onClick: () => navigate("/services"),
    },
    {
      label: "Total Instances",
      value: stats?.totalInstancesCreated ?? (instances || []).length,
      icon: <DnsIcon sx={{ fontSize: 28 }} />,
      color: "#8b5cf6",
      bgFrom: "#f5f3ff",
      bgTo: "#ede9fe",
      border: "#ddd6fe",
      onClick: () => navigate("/instances"),
    },
    {
      label: "Active Instances",
      value: `${stats?.activeInstanceCount ?? activeCount} / ${maxInstances}`,
      icon: <PlayCircleIcon sx={{ fontSize: 28 }} />,
      color: "#10b981",
      bgFrom: "#f0fdf4",
      bgTo: "#dcfce7",
      border: "#bbf7d0",
      onClick: () => navigate("/instances"),
    },
    {
      label: "Expiring Soon",
      value: expiringInstances.length,
      icon: <TimerIcon sx={{ fontSize: 28 }} />,
      color: expiringInstances.length > 0 ? "#f59e0b" : "#6b7280",
      bgFrom: expiringInstances.length > 0 ? "#fffbeb" : "#f9fafb",
      bgTo: expiringInstances.length > 0 ? "#fef3c7" : "#f3f4f6",
      border: expiringInstances.length > 0 ? "#fde68a" : "#e5e7eb",
      onClick: () => navigate("/instances"),
    },
  ];

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
      {/* Welcome */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontWeight: 700,
            color: "text.primary",
          }}
        >
          Welcome back{user?.email ? `, ${user.email.split("@")[0]}` : ""}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: "text.secondary",
          }}
        >
          Here's an overview of your Docker services and instances.
        </Typography>
      </Box>
      {/* Stats Grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "1fr 1fr 1fr 1fr" },
          gap: 2,
          mb: 4,
        }}
      >
        {isLoading
          ? [1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={120}
                sx={{ borderRadius: 3 }}
              />
            ))
          : topStats.map((stat) => (
              <Paper
                key={stat.label}
                onClick={stat.onClick}
                sx={{
                  p: 3,
                  cursor: "pointer",
                  background: `linear-gradient(135deg, ${stat.bgFrom}, ${stat.bgTo})`,
                  border: `1px solid ${stat.border}`,
                  transition: "all 0.2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        textTransform: "uppercase",
                        color: stat.color,
                      }}
                    >
                      {stat.label}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: stat.color,
                        mt: 1,
                      }}
                    >
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      bgcolor: stat.border,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: stat.color,
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
              </Paper>
            ))}
      </Box>
      {/* Alerts */}
      {failedInstances.length > 0 && !dismissedAlerts.has("failed") && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() =>
            setDismissedAlerts((prev) => new Set(prev).add("failed"))
          }
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/instances")}
            >
              View
            </Button>
          }
        >
          {failedInstances.length} instance
          {failedInstances.length > 1 ? "s" : ""} failed to provision.
        </Alert>
      )}
      {expiringInstances.length > 0 && !dismissedAlerts.has("expiring") && (
        <Alert
          severity="warning"
          icon={<WarningAmberIcon />}
          sx={{ mb: 2 }}
          onClose={() =>
            setDismissedAlerts((prev) => new Set(prev).add("expiring"))
          }
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate("/instances")}
            >
              View
            </Button>
          }
        >
          {expiringInstances.length} instance
          {expiringInstances.length > 1 ? "s" : ""} expiring within the hour.
        </Alert>
      )}
      {/* Your Resource Usage */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "text.primary",
            mb: 3,
          }}
        >
          Your Resource Usage
        </Typography>

        {statsLoading ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
              gap: 2,
            }}
          >
            {[1, 2, 3].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={80}
                sx={{ borderRadius: 2 }}
              />
            ))}
          </Box>
        ) : stats ? (
          <>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
                gap: 2,
                mb: 3,
              }}
            >
              <Box sx={{ bgcolor: "action.hover", borderRadius: 2, p: 2.5 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <AccessTimeIcon sx={{ fontSize: 18, color: "#3b82f6" }} />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      color: "text.secondary",
                    }}
                  >
                    Hours Consumed
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#3b82f6",
                  }}
                >
                  {stats.totalHoursConsumed}h
                </Typography>
              </Box>
              <Box sx={{ bgcolor: "action.hover", borderRadius: 2, p: 2.5 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <CategoryIcon sx={{ fontSize: 18, color: "#8b5cf6" }} />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      color: "text.secondary",
                    }}
                  >
                    Services Used
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#8b5cf6",
                  }}
                >
                  {stats.distinctServicesUsed}
                </Typography>
              </Box>
              <Box sx={{ bgcolor: "action.hover", borderRadius: 2, p: 2.5 }}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <DnsIcon sx={{ fontSize: 18, color: "#10b981" }} />
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 600,
                      textTransform: "uppercase",
                      color: "text.secondary",
                    }}
                  >
                    Total Created
                  </Typography>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#10b981",
                  }}
                >
                  {stats.totalInstancesCreated}
                </Typography>
              </Box>
            </Box>

            {/* Instance Quota Bar */}
            <Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: "text.secondary",
                  }}
                >
                  Instance Quota
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "text.primary",
                  }}
                >
                  {stats.activeInstanceCount} / {maxInstances}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={usagePercent}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: "divider",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 4,
                    bgcolor:
                      usagePercent >= 80
                        ? "#ef4444"
                        : usagePercent >= 60
                          ? "#f59e0b"
                          : "#10b981",
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  mt: 0.5,
                  display: "block",
                }}
              >
                {maxInstances - stats.activeInstanceCount} instance
                {maxInstances - stats.activeInstanceCount !== 1 ? "s" : ""}{" "}
                remaining
              </Typography>
            </Box>
          </>
        ) : (
          <Alert severity="info" variant="outlined">
            Resource stats unavailable. Please try again later.
          </Alert>
        )}
      </Paper>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
        }}
      >
        {/* Active Instances */}
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "text.primary",
              }}
            >
              Active Instances
            </Typography>
            <Button
              size="small"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate("/instances")}
            >
              View All
            </Button>
          </Box>
          {instancesLoading ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {[1, 2].map((i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={72}
                  sx={{ borderRadius: 2 }}
                />
              ))}
            </Box>
          ) : (activeInstances || []).length > 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {(activeInstances || [])
                .slice(0, 4)
                .map((instance: ServiceInstance) => (
                  <InstanceCard key={instance.id} instance={instance} />
                ))}
            </Box>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                bgcolor: "action.hover",
                borderRadius: 2,
              }}
            >
              <DnsIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                No active instances
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => navigate("/services")}
                sx={{ mt: 1.5 }}
              >
                Launch one
              </Button>
            </Box>
          )}
        </Paper>

        {/* Quick Launch */}
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "text.primary",
              }}
            >
              Quick Launch
            </Typography>
            <Button
              size="small"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate("/services")}
            >
              All Services
            </Button>
          </Box>
          {servicesLoading ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
              }}
            >
              {[1, 2, 3, 4].map((i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={48}
                  sx={{ borderRadius: 2 }}
                />
              ))}
            </Box>
          ) : services.length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 1.5,
              }}
            >
              {services.slice(0, 8).map((service) => (
                <Box
                  key={service.id}
                  onClick={() => navigate(`/services/${service.id}`)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontWeight: 500,
                      color: "text.primary",
                    }}
                  >
                    {service.name}
                  </Typography>
                </Box>
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                py: 4,
                bgcolor: "action.hover",
                borderRadius: 2,
              }}
            >
              <AppsIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                }}
              >
                No services available
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default DashboardPage;
